import { PACK_DEFINITIONS, getPackAccess } from './packSystem';
import { sha256HexSync } from './sha256Sync';
import { clientIdentityFields, hasClientIdentity } from './clientIdentity';
import { clientAppFetch } from './clientAppFetch';
import { supabase } from '../supabaseClient';

function validIso(value) {
  return typeof value === 'string'
    && value.length > 0
    && Number.isFinite(Date.parse(value));
}

function uuidFromSha256(hex) {
  const chars = hex.slice(0, 32).split('');
  chars[12] = '5';
  chars[16] = ['8', '9', 'a', 'b'][Number.parseInt(chars[16], 16) % 4];
  const raw = chars.join('');
  return `${raw.slice(0, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}-${raw.slice(20)}`;
}

/**
 * Construit l'autorisation commerciale à la source, dans le SaaS d'Anissa.
 * L'app cliente ne reçoit jamais la définition du pack et ne peut donc pas
 * décider elle-même qu'un accompagnement est terminé.
 */
export function buildRenewalOfferCommand(client) {
  const packType = client?.packType || client?.pack_type;
  const pack = PACK_DEFINITIONS[packType];
  const access = getPackAccess(packType);
  const packStartedAt = client?.packStartedAt || client?.pack_started_at;
  const packStartedAtConfirmed = client?.packStartedAtConfirmed
    ?? client?.pack_started_at_confirmed;
  const schedule = client?.packSchedule || client?.pack_schedule;

  if (!client?.id || !pack || !access.continuousFollowup
      || !Array.isArray(pack.steps) || pack.steps.length === 0
      || packStartedAtConfirmed !== true || !validIso(packStartedAt)
      || !Array.isArray(schedule)) {
    return null;
  }

  const startMs = Date.parse(packStartedAt);
  const completedSteps = [];
  if (schedule.length !== pack.steps.length) return null;
  for (let stepNumber = 1; stepNumber <= pack.steps.length; stepNumber += 1) {
    const matches = schedule.filter((entry) => entry?.stepNumber === stepNumber);
    if (matches.length !== 1) return null;
    const [stored] = matches;
    if (stored?.status !== 'done' || !validIso(stored?.completedAt)) return null;
    if (Date.parse(stored.completedAt) < startMs) return null;
    const previous = completedSteps.at(-1);
    if (previous && Date.parse(stored.completedAt) < Date.parse(previous.completedAt)) return null;
    completedSteps.push(stored);
  }

  const visibleFrom = completedSteps.at(-1).completedAt;
  const periodDigest = sha256HexSync(JSON.stringify({
    version: 1,
    client_id: client.id,
    pack_type: packType,
    pack_started_at: new Date(packStartedAt).toISOString(),
  }));
  const sourcePeriodId = `period-v1-${periodDigest}`;
  const offerDigest = sha256HexSync(sourcePeriodId);

  return {
    ...clientIdentityFields(client),
    offer: {
      version: 1,
      id: uuidFromSha256(offerDigest),
      kind: 'continue_followup',
      status: 'available',
      source_period_id: sourcePeriodId,
      visible_from: new Date(visibleFrom).toISOString(),
    },
  };
}

export function matchesScheduledReview(review, scheduleEntry) {
  return Boolean(
    review?.id
    && scheduleEntry?.reviewId
    && review.id === scheduleEntry.reviewId
    && review.client_id
    && Number.isInteger(review.step_number)
    && review.step_number === scheduleEntry.stepNumber
    && validIso(review.submitted_at)
  );
}

export async function publishRenewalOfferIfEligible(client, {
  publish = clientAppFetch,
  loadCanonical = async (clientId) => {
    const { data, error } = await supabase
      .from('clients')
      .select('id, form, staging_client_id, pack_type, pack_started_at, pack_started_at_confirmed, pack_schedule, is_deleted')
      .eq('id', clientId)
      .eq('is_deleted', false)
      .maybeSingle();
    if (error) throw error;
    return data;
  },
} = {}) {
  if (!client?.id) return { published: false, reason: 'client_missing' };
  const canonical = await loadCanonical(client.id);
  if (!canonical) return { published: false, reason: 'canonical_missing' };
  if (canonical.is_deleted !== false) return { published: false, reason: 'client_deleted' };
  if (!hasClientIdentity(canonical)) return { published: false, reason: 'identity_missing' };
  const payload = buildRenewalOfferCommand(canonical);
  if (!payload) return { published: false, reason: 'not_eligible' };
  await publish('/api/admin/renewal-offer', { method: 'POST', payload });
  return { published: true, offerId: payload.offer.id };
}
